import isEmpty from 'transmute/isEmpty';

// @ts-expect-error untyped module customer-data-objects-ui-components
import ObjectTypeIdType from 'customer-data-objects-ui-components/propTypes/ObjectTypeIdType';
import PropTypes from 'prop-types';
// @ts-expect-error untyped module reference-resolvers
import * as ReferenceObjectTypes from 'reference-resolvers/constants/ReferenceObjectTypes';
// @ts-expect-error untyped module reference-resolvers
import * as ReferenceTypes from 'reference-resolvers/schema/ReferenceTypes';
// @ts-expect-error untyped module reference-resolvers
import ResolveReferences from 'reference-resolvers/ResolveReferences';
// @ts-expect-error untyped module reference-resolvers
import { isLoading, isResolved } from 'reference-resolvers/utils';
// @ts-expect-error untyped module reference-resolvers

import memoize from 'transmute/memoize';
import { getObjectTypePropertyLabel } from 'automation-ui-references/object-type-properties/getObjectTypePropertyLabel';
import { isDefaultEmptyPropertyValue } from 'automation-ui-references/object-type-properties/PropertyValueDefaults';
import UILink from 'UIComponents/link/UILink';
import MissingLabel from 'automation-ui-core/components/MissingLabel';
import SkeletonText from 'automation-ui-core/components/SkeletonText';
import EditPropertyLink from 'automation-ui-references/object-type-properties/EditPropertyLink';
import { jsx as _jsx } from "react/jsx-runtime";
const noop = () => {};
/**
 * Example (where 0-3 is a Deal Object and DEAL_OBJECT_ID = "0-3"):
 *   <PropertyName objectTypeId={DEAL_OBJECT_ID} propertyName="dealstage" />
 * Renders as:
 *   Deal stage
 *  OR
 * Example (where 0-3 is a Deal Object, DEAL_OBJECT_ID = "0-3", and href is an external link):
 *   <PropertyName objectTypeId={DEAL_OBJECT_ID} propertyName="dealstage" href={href}/>
 * Renders as external link:
 *   Deal stage
 */
const propTypes = {
  /**
   * href is used as an override to render a simple link instead of the
   * EditProertyLink. If more use cases are needed, we should consider
   * refactoring this component to take a renderContent prop.
   */
  href: PropTypes.string,
  objectTypeId: ObjectTypeIdType.isRequired,
  propertyName: PropTypes.string.isRequired,
  // Provided by ResolveReferences
  property: PropTypes.oneOfType([ReferenceTypes.byId, PropTypes.object]).isRequired,
  refreshCache: PropTypes.func.isRequired
};
const PropertyNameView = ({
  href,
  objectTypeId,
  property,
  propertyName,
  refreshCache
}) => {
  if (isEmpty(property)) {
    return /*#__PURE__*/_jsx(MissingLabel, {
      type: "property"
    });
  }
  if (isLoading(property)) {
    return /*#__PURE__*/_jsx(SkeletonText, {});
  } else if (isResolved(property)) {
    if (href) {
      return /*#__PURE__*/_jsx(UILink, {
        external: true,
        href: href,
        children: getObjectTypePropertyLabel(property.id, property.label, objectTypeId)
      });
    }
    return /*#__PURE__*/_jsx(EditPropertyLink, {
      objectTypeId: objectTypeId,
      onSave: refreshCache,
      propertyName: propertyName,
      children: getObjectTypePropertyLabel(property.id, property.label, objectTypeId)
    });
  }
  return /*#__PURE__*/_jsx(MissingLabel, {
    internalName: propertyName,
    type: "property"
  });
};
PropertyNameView.propTypes = propTypes;
const memoizedReferenceResolver = memoize((objectTypeId, resolverFactory, resolversCache) => resolverFactory(objectTypeId, {})(resolversCache));
const memoizedRefreshCache = memoize((refreshCacheFunc, cacheId) => () => refreshCacheFunc(cacheId));
/**
 * This resolver needs to be provided similar to the example below because it
 * requires extra arguments for instantiation.
 *
 * ProvideReferenceResolvers({
 *   [OBJECT_PROPERTIES]: resolversCache => () => ({
 *     resolversCache,
 *     resolverFactory: ObjectPropertiesReferenceResolver,
 *   }),
 * });
 */
const mapResolversToProps = (resolvers, ownProps) => {
  const {
    objectTypeId,
    propertyName
  } = ownProps;
  const {
    resolverFactory,
    resolversCache
  } = resolvers[ReferenceObjectTypes.OBJECT_PROPERTIES]();
  const PropertyReferenceResolver = memoizedReferenceResolver(objectTypeId, resolverFactory, resolversCache);

  /**
   * Don't try to resolve empty or default properties as they are invalid
   * and shouldn't attempt to be resolved.
   */
  if (!propertyName || isDefaultEmptyPropertyValue(propertyName)) {
    return {
      property: {},
      refreshCache: noop
    };
  }
  return {
    property: PropertyReferenceResolver.byId(propertyName),
    refreshCache: memoizedRefreshCache(PropertyReferenceResolver.refreshCache, propertyName)
  };
};
const PropertyName = ResolveReferences(mapResolversToProps)(PropertyNameView);
export default PropertyName;